import React, { Attributes, forwardRef, HTMLAttributes, RefObject, useEffect, useMemo, useRef } from 'react';
import { styled } from '@mui/system';
import Autocomplete, { AutocompleteProps, createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { ProjectSections } from '@bimdata/definitions/const';

const CustomStyledAutocomplete = styled(Autocomplete<string, false, true, true>)(() => ({
  minWidth: 90,
  '&.MuiAutocomplete-hasPopupIcon': {
    '.MuiInputBase-root': {
      padding: '0 30px 0 0',
    },
    '.MuiAutocomplete-endAdornment': {
      right: 7,
    },
    '.MuiInputBase-input': {
      padding: '0px 0px 0px 14px',
      height: '27.5px',
    }
  },
}));

const Group = styled('li')({
  '&:not(:first-of-type)': {
    borderTop: '1px solid #0000003b',
  }
});
const GroupItems = styled('ul')({
  padding: 0,
});

type AssignedProps = 'onChange' | 'options' | 'filterOptions' | 'groupBy' | 'renderInput' | 'renderGroup' | 'renderOption' | 'value' | 'ref';

export type SectionSelectProps = Omit<AutocompleteProps<string, false, true, true>, AssignedProps> & {
  value?: string | null;
  additionalSections: Array<string>;
  onChange: (section: string | null) => void | Promise<void>;
  onCustomSection: (section: string) => void | Promise<void>;
  onReady?: (root: HTMLDivElement) => void;
  inputRef?: RefObject<HTMLInputElement | null>;
};

const filterSections = createFilterOptions<string>();

const MainSections = Object.values<string>(ProjectSections);

export default forwardRef(({ value, additionalSections, onChange, onCustomSection, disabled = false, onReady, inputRef, ...props }: SectionSelectProps, ref: RefObject<HTMLDivElement>) => {
  const mainRef = useRef<HTMLDivElement>(null);
  
  const options = useMemo(() => ([
    ...MainSections,
    ...additionalSections
  ]), [ additionalSections ]);

  useEffect(() => {
    if (mainRef.current) {
      if (ref) {
        ref.current = mainRef.current;
      }
      if (onReady) {
        onReady(mainRef.current);
      }
    }
  }, [mainRef.current]);

  const optionGroup = (o: string) => o ? ((MainSections.includes(o) ? 1 : 2)) : 0;

  return <CustomStyledAutocomplete
    {...props}
    ref={mainRef}
    freeSolo
    selectOnFocus
    clearOnBlur
    forcePopupIcon
    disableClearable
    // componentsProps={{ popper: { style: { width: 'fit-content' } } }}
    value={value || ''}
    disabled={disabled}
    clearIcon={null}
    onChange={async (e, v, r) => {
      let section = v || null;
      if (section) {
        const existing = options.find((o) => (o.toUpperCase() === section?.toUpperCase()));
        if (!existing) {
          await onCustomSection(section);
        } else {
          section = existing;
        }
      }
      await onChange(section);
    }}
    options={options}
    groupBy={(o) => `g${optionGroup(o)}`}
    filterOptions={(opts, params) => {
      const filtered = filterSections(opts, params);

      const { inputValue } = params;
      if (inputValue) {
        filtered.push('');

        if(!opts.some((o) => (o.toUpperCase() === inputValue.toUpperCase()))) {
          filtered.unshift(inputValue);
        }
      } else {
        filtered.unshift('');
      }

      return filtered;
    }}
    isOptionEqualToValue={() => false}
    renderInput={(params) => {
      const originalChangeHandler = params.inputProps.onChange;
      params.inputProps.onChange = function (e) {
        e.target.value = e.target.value.trim();
        if (e.target.value.length > 20) {
          e.target.value = e.target.value.substring(0, 20);
        }
        originalChangeHandler && originalChangeHandler.apply(this, [ e ]);
      };

      return <TextField {...params} inputRef={inputRef}/>
    }}
    renderGroup={(params) => {
        return <Group key={params.key}>
          <GroupItems>{params.children}</GroupItems>
        </Group>
      }
    }
    renderOption={(props, option, state, ownerState) => {
      let label = option;
      if (option && !options.some((o) => (o.toUpperCase() === option.toUpperCase()))) {
        label = `+\u00A0"${option}"`;
      }

      const { key, ...itemProps } = props as (Attributes & HTMLAttributes<HTMLLIElement>);
      itemProps['aria-selected'] = option.toUpperCase() === state.inputValue.toUpperCase();
      return <li key={key} {...itemProps}>{label}</li>
    }}
  />;
});

